import {
  Session,
  SupabaseClient,
  User as SupabaseUser,
} from '@supabase/supabase-js';

import { AuthError } from '../../../types';

/**
 * OAuth authentication service
 * Handles Google and Apple OAuth flows
 */
export class OAuthService {
  private supabase: SupabaseClient;

  constructor(supabaseClient: SupabaseClient) {
    this.supabase = supabaseClient;
  }

  // ============================================================================
  // OAuth Authentication
  // ============================================================================

  async signInWithGoogle(baseUrl: string): Promise<{
    success: boolean;
    error?: AuthError;
  }> {
    try {
      const { error } = await this.supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${baseUrl}/auth/callback`,
        },
      });

      if (error) {
        return {
          success: false,
          error: {
            code: 'OAUTH_ERROR',
            message: error.message,
          },
        };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  async signInWithApple(baseUrl: string): Promise<{
    success: boolean;
    error?: AuthError;
  }> {
    try {
      const { error } = await this.supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${baseUrl}/auth/callback`,
        },
      });

      if (error) {
        return {
          success: false,
          error: {
            code: 'OAUTH_ERROR',
            message: error.message,
          },
        };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  // ============================================================================
  // OAuth Token Authentication
  // ============================================================================

  async signInWithCredentials(
    credentials: {
      provider: 'google' | 'apple';
      idToken: string;
      accessToken?: string;
    },
    onSessionCreated?: (session: Session) => Promise<void>
  ): Promise<{
    success: boolean;
    user?: SupabaseUser;
    session?: Session;
    error?: AuthError;
  }> {
    try {
      const { data, error } = await this.supabase.auth.signInWithIdToken({
        provider: credentials.provider,
        token: credentials.idToken,
      });

      if (error) {
        return {
          success: false,
          error: {
            code: 'OAUTH_CREDENTIALS_ERROR',
            message: error.message,
          },
        };
      }

      // Platform-specific session handling
      if (data.session && onSessionCreated) {
        await onSessionCreated(data.session);
      }

      return {
        success: true,
        user: data.user,
        session: data.session,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }
}
